import React, { useEffect, useState } from 'react';
import { useExpanded, useSortBy, useTable } from 'react-table';
import styled from 'styled-components';

interface Props {
  columns: any;
  data: any;
  supportSort?: boolean;
  onClick?: (roomId: number) => void;
}

const ManagerTable = ({ columns, data, supportSort = false, onClick }: Props) => {
  const [selectedRoom, setSelectedRoom] = useState(undefined);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: 'number',
            desc: false,
          },
        ],
      },
    },
    useSortBy,
    useExpanded
  );

  const renderRowSubComponent = React.useCallback(
    ({ row }: any) => (
      <div style={{ margin: 10 }}>
        <h3>요청 구조대 : </h3>
        <h3>연결 구조대 : </h3>
      </div>
    ),
    []
  );

  const selectRoom = (data: any) => {
    if (onClick !== undefined) {
      // highlightTableRow(data);
      onClick(data.original.roomId);
      setSelectedRoom(data.original.roomId);
    }
  };

  useEffect(() => {
    const previouslySelectedRow = document.querySelector('tr.selected');
    if (previouslySelectedRow) {
      previouslySelectedRow.classList.remove('selected');
    }
    if (selectedRoom !== undefined) {
      const rowIndex = rows.findIndex((data: any) => data.original.roomId === selectedRoom);
      const tableRows = document.querySelectorAll('tbody tr');
      if (rowIndex !== undefined && tableRows[rowIndex]) {
        tableRows[rowIndex].classList.add('selected');
      }
    }
  }, [rows, selectedRoom]);

  return (
    <Styles canClick={!!onClick}>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, i) => (
                <th
                  {...column.getHeaderProps(
                    supportSort ? column.getSortByToggleProps({ title: '정렬 변경' }) : undefined
                  )}
                  style={{ cursor: supportSort && column.canSort ? 'pointer' : 'default' }}
                  key={i}
                >
                  {column.render('Header')}
                  {supportSort && column.canSort ? (
                    column.isSorted ? (
                      column.isSortedDesc ? (
                        <span>&nbsp;&#128317;</span>
                      ) : (
                        <span>&nbsp;&#128316;</span>
                      )
                    ) : (
                      ' ↕'
                    )
                  ) : null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            return (
              <React.Fragment key={row.getRowProps().key}>
                <tr onClick={() => selectRoom(row)}>
                  {row.cells.map((cell, i) => (
                    <td {...cell.getCellProps()} key={i}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
                {/*
                    If the row is in an expanded state, render a row with a
                    column that fills the entire length of the table.
                  */}
                {row.isExpanded ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      {/*
                          Inside it, call our renderRowSubComponent function. In reality,
                          you could pass whatever you want as props to
                          a component like this, including the entire
                          table instance. But for this example, we'll just
                          pass the row
                        */}
                      {renderRowSubComponent({ row })}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Styles>
  );
};

const Styles = styled.div<{ canClick: boolean }>`
  height: 100%;

  table {
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, #002f5a 0%, #000 100%);

    thead {
      display: block;
      table-layout: fixed;
      width: 100%;
      color: #fff;
      text-align: center;
      font-family: Inter;
      font-size: 1rem;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      // border-radius: 30px 30px 0px 0px;
      th {
        vertical-align: middle;
        height: 2.56rem;
        border: 1px solid #6b6b6b;
        span {
          background: transparent;
          border: none;
        }
      }
    }

    tbody {
      height: calc(100% - 90px);
      table-layout: fixed;
      display: block;
      overflow-y: auto;
      overflow-x: hidden;
      // margin: 0 5px 0 5px;
      ::-webkit-scrollbar {
        // display: none;
        width: 3px;
      }
      ::-webkit-scrollbar-thumb {
        height: 10%; /* 스크롤바의 길이 */
        background: #0086ff; /* 스크롤바의 색상 */

        border-radius: 10px;
      }
      ::-webkit-scrollbar-track {
        background: rgba(33, 122, 244, 0.1); /*스크롤바 뒷 배경 색상*/
      }

      tr {
        ${(props) =>
          props.canClick &&
          `
        &:hover {
          background: rgba(255, 255, 255, 0.05);
        }
      `}
      }
    }

    tr {
      display: table;
      width: 100%;
      table-layout: fixed;
      border-bottom: 1px solid #6b6b6b;
      :last-child {
        td {
          border-bottom: 0;
        }
      }
      &.selected {
        background: #004d94;
      }
    }

    td {
      vertical-align: middle;
      height: 2.5rem;
      margin: 0;
      color: #fff;
      text-align: center;
      font-family: Inter;
      font-size: 1rem;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      border: 1px solid #6b6b6b;
      cursor: ${(props) => (props.canClick ? 'pointer' : 'default')};
    }
  }
`;

export default ManagerTable;
